import { NewsPassController } from "@product/rosetta-sdk";
import { useAtomValue } from "jotai/index";
import { useMemo } from "react";
import { graphql, readInlineData } from "react-relay";

import {
  checkArticleIsInArchiveWall,
  checkArticleIsInLiteUpgradeWall,
  checkArticleIsInSignInWall,
  checkArticleIsInVelocityWall,
  checkArticleIsPlusInlinePaywall,
} from "scmp-app/components/article/article-render/hooks/piano-integration/helpers";
import { appAtom } from "scmp-app/lib/app/atoms";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import {
  useLTSHardPaywallABTest,
  useVelocityWallThresholdABTest,
} from "scmp-app/lib/rosetta/a-b-test/hooks";
import { useRosettaCampaign } from "scmp-app/lib/rosetta/hooks/rosetta-campaign";
import { useRosettaConfig } from "scmp-app/lib/rosetta/hooks/rosetta-config";
import { usePaywallStrategies } from "scmp-app/lib/rosetta/hooks/rosetta-paywall-strategies";
import { useRosettaPianoExecution } from "scmp-app/lib/rosetta/hooks/rosetta-piano-execution";
import type { paywallsGetArticlePaywallTagArticle$key } from "scmp-app/queries/__generated__/paywallsGetArticlePaywallTagArticle.graphql";

export const useGetArticlePaywallTag = (article_: paywallsGetArticlePaywallTagArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment paywallsGetArticlePaywallTagArticle on Article @inline {
        flag
        topics {
          entityId
        }
        ...helpersGetArticleTypeArticle
        ...helpersCheckIsPlusArticle
        ...helpersIsSignInWallArticle
        ...helpersIsArchiveWallArticle
        ...helpersIsPlusInlinePaywall
        ...helpersIsLiteWallArticle
        ...helpersIsVelocityWallArticle
        ...hooksVelocityWallThresholdABTest
      }
    `,
    article_,
  );

  const { applyVelocityWallThresholdLogic, shouldApplyVelocityWallThresholdTest } =
    useVelocityWallThresholdABTest(article);

  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { getActiveCampaignSettings } = useRosettaCampaign();
  const { getActivePaywallStrategy } = usePaywallStrategies();
  const { isRosettaPianoExecutionReady } = useRosettaPianoExecution(
    navigatorInfo,
    navigatorInfoLoaded,
  );

  const { ltsHardPaywallABTestTag } = useLTSHardPaywallABTest();
  const { rosettaConfigState } = useRosettaConfig();

  const appSettings = asyncRosettaState?.result?.appSettings ?? {};
  const { litePaywallTimeFrame = 3, oldArticleTimeframe = 5 } = appSettings;

  const isSignInPaywall = checkArticleIsInSignInWall(article);

  const isPlusInlinePaywall = checkArticleIsPlusInlinePaywall(article);

  // Note: can refactor and include checkArticleIsInVelocityWall into this memorized value
  const isVelocityPaywall = useMemo(
    () =>
      shouldApplyVelocityWallThresholdTest
        ? applyVelocityWallThresholdLogic()
        : checkArticleIsInVelocityWall(article),
    [applyVelocityWallThresholdLogic, article, shouldApplyVelocityWallThresholdTest],
  );

  // Note: can refactor and include checkArticleIsInArchiveWall into this memorized value
  const isArchivePaywall = useMemo(() => {
    if (!isRosettaPianoExecutionReady && asyncRosettaState?.status !== "success") return;

    const activeCampaignSettings = getActiveCampaignSettings();
    const activeArchivePaywallStrategy = getActivePaywallStrategy({ paywallType: "archive" });

    const archiveArticleTimeFrame =
      activeCampaignSettings?.settings?.paywall?.archive?.articleAgeThresholdDays ??
      activeArchivePaywallStrategy?.settings?.paywall?.archive?.articleAgeThresholdDays ??
      oldArticleTimeframe;

    return checkArticleIsInArchiveWall(article, archiveArticleTimeFrame);
  }, [
    article,
    asyncRosettaState?.status,
    getActiveCampaignSettings,
    getActivePaywallStrategy,
    isRosettaPianoExecutionReady,
    oldArticleTimeframe,
  ]);

  const isScmpLiteUser = asyncRosettaState?.result?.user?.isSCMPLiteSubscriber ?? false;
  const isLiteUpgradePaywall = useMemo(
    () => checkArticleIsInLiteUpgradeWall(article, isScmpLiteUser, litePaywallTimeFrame),
    [article, isScmpLiteUser, litePaywallTimeFrame],
  );

  const hasUsedNewsPassTrialOffer =
    asyncRosettaState?.result?.user?.newsPass?.hasUsedOffer ?? false;
  const isNewsPassTrialActive = asyncRosettaState?.result?.user?.newsPass?.isTrialActive ?? false;
  const isSCMPChurnedUser = asyncRosettaState?.result?.user?.isSCMPChurned ?? false;
  const isSCMPSubscriber = asyncRosettaState?.result?.isScmpSubscriber ?? false;

  const newsPassOfferWallTag = useMemo(
    () =>
      NewsPassController.getNewsPassPianoTagIfUserQualifies({
        hasUsedNewsPassTrialOffer,
        isNewsPassOfferEnabled:
          rosettaConfigState.result?.featureFlags?.isNewsPassOfferPayWallEnabled ?? false,
        isNewsPassTrialActive,
        isSCMPChurned: isSCMPChurnedUser,
        isSCMPSubscriber,
      }),
    [
      rosettaConfigState.result?.featureFlags?.isNewsPassOfferPayWallEnabled,
      hasUsedNewsPassTrialOffer,
      isNewsPassTrialActive,
      isSCMPSubscriber,
      isSCMPChurnedUser,
    ],
  );

  const pianoArticlePaywallCategoryTag = (() => {
    if (!isRosettaPianoExecutionReady && asyncRosettaState?.status !== "success") return;

    const topicIds = article.topics?.map?.(topic => topic?.entityId ?? "") ?? [];
    const paywallStrategySettings = getActivePaywallStrategy({
      flag: article?.flag ?? undefined,
      topicIds,
    });

    if (isPlusInlinePaywall) {
      return "category/plusChinaFutureTech";
    }
    if (newsPassOfferWallTag) return newsPassOfferWallTag;
    if (isLiteUpgradePaywall) return "category/liteUpgrade";

    // TODO: remove the type loosening when rosetta sdk add back signInWall to PaywallType
    const paywallStrategySettingsPaywallType: string | undefined =
      paywallStrategySettings?.settings?.paywall?.type;

    if (ltsHardPaywallABTestTag) return ltsHardPaywallABTestTag;
    if (paywallStrategySettingsPaywallType && paywallStrategySettingsPaywallType !== "signInWall")
      return `category/${paywallStrategySettingsPaywallType}`;
    if (isVelocityPaywall) return "category/velocity";
    if (isArchivePaywall) return "category/archive";
    if (isSignInPaywall) return "category/signInWall";
    if (paywallStrategySettingsPaywallType) return `category/${paywallStrategySettingsPaywallType}`;
  })();

  return {
    pianoArticlePaywallCategoryTag,
  };
};
